import { createTheme, responsiveFontSizes } from '@mui/material';

const baseTheme = createTheme();

const muiTheme = responsiveFontSizes(createTheme({
    palette: {
        primary: {
            main: '#37474F', // Dark grey for primary elements
            light: '#455A64',
            dark: '#324148',
            contrastText: '#FFFFFF'
        },
        secondary: {
            main: '#D01C53', // Pink/red for active states and accents
            light: '#E43A6D',
            dark: '#B91746',
            contrastText: '#FFFFFF'
        },
        background: {
            default: '#FFFFFF',
            paper: '#FFFFFF',
            navigation: '#F5F6F7',
            subtle: '#F5F7F8'
        },
        text: {
            primary: '#37474F',
            secondary: '#607D8B'
        },
        divider: '#E5E7EB',
        inputBorder: '#CFD8DC',
        success: {
            main: '#7BCF93',
            light: '#78CE91',
            dark: '#5AC478'
        }
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif'
        },
        h2: {
            fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif'
        },
        h3: {
            fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif'
        },
        h4: {
            fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1.8rem'
        },
        h5: {
            fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            lineHeight: 1.4

            // '@media (min-width:600px)': {
            //     fontSize: '1.5rem',
            //     lineHeight: 1
            // }
        },
        h6: {
            fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '0.05px'
        },
        subtitle1: {
            fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            lineHeight: 1.5
        },
        button: {
            lineHeight: '24px'
        }
    },
    spacing: 4,
    shape: {
        borderRadius: 4,
        borderRadiusLg: 8
    },
    shadows: {
        ...baseTheme.shadows,
        2: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)'
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    textTransform: 'none',

                    minHeight: 44,
                    paddingTop: 10,
                    paddingBottom: 10,

                    [theme.breakpoints.up('sm')]: {
                        minHeight: 36,
                        paddingTop: 6,
                        paddingBottom: 6
                    }
                }),
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none'
                    }
                },
                containedSecondary: {
                    backgroundColor: '#D01C53',
                    color: '#FFFFFF',
                    '&:hover': {
                        backgroundColor: '#B91746'
                    }
                },
                startIcon: {
                    fontSize: '1.2rem',

                    '& > *:first-child': {
                        fontSize: '1.2rem'
                    }
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    minHeight: 48,
                    color: '#607D8B',
                    '&.Mui-selected': {
                        color: '#D01C53'
                    }
                }
            }
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: '#D01C53',
                    height: 3
                }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: `0 1px 0 0 ${ theme.palette.action.selected }`,
                    borderRadius: 0
                })
            }
        },
        MuiToolbar: {
            styleOverrides: {
                root: ({ theme }) => ({
                    [theme.breakpoints.up('sm')]: {
                        minHeight: theme.spacing(14)
                    }
                })
            }
        },
        MuiFormControl: {
            defaultProps: {
                size: 'small'
            }
        },
        MuiFormControlLabel: {
            defaultProps: {
                slotProps: {
                    typography: {
                        variant: 'body2'
                    }
                }
            }
        },
        MuiCheckbox: {
            defaultProps: {
                size: 'small'
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(1)
                })
            }
        },
        MuiSkeleton: {
            defaultProps: {
                variant: 'rounded',
                height: 16
            },
            styleOverrides: {
                root: {
                    marginTop: 4,
                    marginBottom: 4
                },
                rounded: {
                    borderRadius: 8
                }
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: theme.spacing(2, 4, 4)
                })
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: () => ({
                    fontSize: '1.1rem',
                    lineHeight: '2rem'
                })
            }
        },
        MuiAlert: {
            defaultProps: {
                severity: 'info',
                variant: 'standard'
            },
            variants: [
                {
                    props: { severity: 'info', variant: 'standard' },
                    style: ({ theme }) => ({
                        backgroundColor: theme.palette.background.navigation,
                        color: theme.palette.text.primary,
                        '& .MuiAlert-icon': {
                            color: theme.palette.text.primary
                        }
                    })
                }
            ]
        },
        MuiRadio: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(1)
                })
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadiusLg
                })
            }
        },
        MuiSvgIcon: {
            defaultProps: {
                fontSize: 'medium'
            },
            variants: [
                { 
                    props: { fontSize: 'small' },
                    style: {
                        fontSize: '1rem'
                    }
                },
                { 
                    props: { fontSize: 'medium' },
                    style: {
                        fontSize: '1.3rem'
                    }
                }
            ]
        }
    }
}));

export default muiTheme;
